import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
/**
 * @type {'polite' | 'assertive'}
 */
export const ARIA_LIVE_TYPES = ['polite', 'assertive'];
const StyledMessage = styled.div `
  align-self: flex-start;
  border-radius: 0.1875rem;
  display: inline-flex;
  font-size: 0.8125rem;
  justify-content: center;
  line-height: 1.38;
  min-width: 3.75rem;
  padding: 0.25rem 0.78125rem;
  position: relative;
  color: ${props => props.theme.White};
  background-color: ${props => props.theme.BrandPrimary};

  &:before {
    border-left: ${props => props.theme.caretWidth}px solid transparent;
    border-right: ${props => props.theme.caretWidth}px solid transparent;
    content: '';
    height: 0;
    left: ${props => props.theme.caretWidth * 2}px;
    position: absolute;
    top: -${props => props.theme.caretWidth}px;
    width: 0;
    border-bottom: ${props => props.theme.caretWidth}px solid transparent;
    border-bottom-color: ${props => props.theme.BrandPrimary};
  }
`;
/**
 * Renders an error message component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.ariaLive] - The value for the `aria-live` attribute.
 * @param {ReactNode} props.children - The content of the error message.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {string} [props.dataTestId='error-message'] - The data-testid attribute value.
 * @param {string} [props.id] - The id attribute value.
 * @returns {JSX.Element} The rendered ErrorMessage component.
 */
export const ErrorMessage = ({ ariaLive = undefined, children, className = undefined, dataTestId = 'error-message', id = undefined, }) => (React.createElement(StyledMessage, { className: className, "data-testid": dataTestId, id: id, "aria-live": ariaLive }, children));
ErrorMessage.propTypes = {
    /**
     * Aria-live description, which can be read by assistive technology during live changes.
     * You will always need this if a client side validation has been implemented.
     * Values are:
     * - `polite`: specifies that the screenreader should wait until the user is no longer busy before issuing the updates. (e.g. )
     * - `assertive`: specifies that the screenreader reads the update immediately.
     * - `off`: default
     */
    ariaLive: PropTypes.oneOf(ARIA_LIVE_TYPES),
    /**
     * node children
     */
    children: PropTypes.node.isRequired,
    /**
     * Allow using styled() wrapper on this component. Don't use this to set a class manually!
     */
    className: PropTypes.string,
    /**
     * Overwrite the internal data-testid for testing purposes.
     */
    dataTestId: PropTypes.string,
    /**
     * Id for the message to link it to the faulty element
     */
    id: PropTypes.string,
};
